import React from 'react';
import {Box, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {useTranslation} from 'react-i18next';
import {ImageInput, ImageValue} from '../../../models/Template';
import {FillField} from './fieldList';
import {layerSubtitle} from '../../../scene/userLayers';
import NumberChip from './NumberChip';

/** Card background — also the NumberChip border color so the chip pops. */
const CARD_BG = '#0a1f33';
/** A compact card sits INSIDE a row card, so it takes the input surface. */
const COMPACT_BG = '#071826';

const Card = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'compact',
})<{ highlighted?: boolean; compact?: boolean }>(({theme, highlighted, compact}) => ({
    border: highlighted ? '1px solid #00E162' : `1px solid ${compact ? '#ffffff30' : '#ffffff50'}`,
    boxShadow: highlighted ? '0 0 0 2px #00E16266' : 'none',
    borderRadius: compact ? '12px' : '14px',
    padding: theme.spacing(compact ? 1.25 : 1.5),
    marginBottom: theme.spacing(compact ? 1 : 1.5),
    backgroundColor: compact ? COMPACT_BG : CARD_BG,
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
}));

const Thumb = styled('img')({
    width: '56px',
    height: '56px',
    borderRadius: '10px',
    objectFit: 'cover',
    border: '1px solid #ffffff50',
    flexShrink: 0,
});

interface FieldCardProps {
    field: FillField;
    /** 1-based checklist number — matches the canvas marker chip. */
    number: number;
    value: string | ImageValue | undefined;
    filled: boolean;
    highlighted: boolean;
    /** Tapping a text card opens the text bottom sheet (edit happens there,
     *  not inline, so the canvas above stays visible while typing). */
    onTextTap: (field: FillField) => void;
    onImageTap: (field: FillField) => void;
    /** A field inside a row card: denser, on the row card's inner surface. */
    compact?: boolean;
}

/** One checklist card. The leading number chip mirrors the canvas marker (blue
 *  number → green ✓). Whole card is tappable: text opens the text sheet, image
 *  opens the action sheet (upload / gallery / reposition). */
const FieldCard = React.forwardRef<HTMLDivElement, FieldCardProps>(({
                                                                        field,
                                                                        number,
                                                                        value,
                                                                        filled,
                                                                        highlighted,
                                                                        onTextTap,
                                                                        onImageTap,
                                                                        compact,
                                                                    }, ref) => {
    const {t} = useTranslation();
    const chipSize = compact ? 22 : 26;
    const chipBorder = compact ? COMPACT_BG : CARD_BG;

    if (field.type === 'image') {
        const imageValue = value as ImageValue | undefined;
        // A user LAYER has no template input: its thumbnail is whatever the user
        // picked (an image layer cannot exist without one) and its name is the
        // layer label, not a placeholder.
        const imageInput = field.input as ImageInput | undefined;
        return (
            <Card ref={ref} highlighted={highlighted} compact={compact} onClick={() => onImageTap(field)}
                  sx={{cursor: 'pointer', '&:active': {backgroundColor: '#0e2942'}}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    <NumberChip key={filled ? 'done' : 'todo'} number={number} done={filled}
                                layer={field.source === 'layer'} chipSize={chipSize} borderColor={chipBorder}/>
                    <Thumb src={imageValue?.thumbnail ?? imageInput?.default?.thumbnail} alt={field.label} loading="lazy"
                           sx={compact ? {width: 44, height: 44} : undefined}/>
                    <Box sx={{flexGrow: 1, minWidth: 0}}>
                        <Typography sx={{color: 'white', fontWeight: 600, fontSize: '0.95rem'}} noWrap>
                            {field.label}
                        </Typography>
                        {/* A LAYER shows the asset the user picked; a template
                            field keeps its "tap to replace" hint. */}
                        <Typography dir="auto" sx={{color: '#C3F3FD', fontSize: '0.8rem'}} noWrap
                                    data-testid={field.source === 'layer' ? `layer-subtitle-${field.key}` : undefined}>
                            {field.source === 'layer' && field.layer
                                ? layerSubtitle(field.layer, imageValue as any, t('editor.layers.emptyText'), t('editor.fillMode.replaceImage'))
                                : t('editor.fillMode.replaceImage')}
                        </Typography>
                    </Box>
                    <ChevronLeftIcon sx={{color: '#C3F3FD'}}/>
                </Box>
            </Card>
        );
    }

    const textValue = ((value as string) ?? '').trim();
    // Show the current value; fall back to the field label when empty. Muted
    // until the field is filled (changed from its default). A LAYER's line is
    // capped so a long paragraph doesn't stretch the card.
    const display = field.source === 'layer' && field.layer
        ? layerSubtitle(field.layer, value as any, t('editor.layers.emptyText'), t('editor.fillMode.replaceImage'))
        : (textValue || field.label);
    return (
        <Card ref={ref} highlighted={highlighted} compact={compact} onClick={() => onTextTap(field)}
              sx={{cursor: 'pointer', '&:active': {backgroundColor: '#0e2942'}}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <NumberChip key={filled ? 'done' : 'todo'} number={number} done={filled}
                                layer={field.source === 'layer'} chipSize={chipSize} borderColor={chipBorder}/>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Typography sx={{color: 'white', fontWeight: 600, fontSize: '0.95rem'}} noWrap>
                        {field.label}
                    </Typography>
                    <Typography sx={{color: filled ? '#C3F3FD' : '#7fa1ba', fontSize: '0.85rem'}} dir="auto" noWrap
                                data-testid={field.source === 'layer' ? `layer-subtitle-${field.key}` : undefined}>
                        {display}
                    </Typography>
                </Box>
                <ChevronLeftIcon sx={{color: '#C3F3FD'}}/>
            </Box>
        </Card>
    );
});

// Memoized so typing in one field doesn't re-render every other checklist card
// (FillModeEditor passes stable field/handlers; only value/filled change per card).
export default React.memo(FieldCard);
